'use client'

import React from 'react'
import {
  Box,
  Container,
  Grid,
  GridItem,
  HStack,
  Heading,
  Text,
  VStack,
  Button,
  BoxProps,
  Stack,
  ButtonProps
} from '@chakra-ui/react'
import ExclusiveOffer from './exclusive-offer'
import ParticipatingOutlets from './participating-outlets'
import FastResult from './fast-result'
import Videos from './videos'
import Certification from './certification'
import HowToUse from './how-to-use'
import Tips from './tips'
import QA from './qa'
import Footer from './footer'
import ellixiLogo from '@images/brands/cheongdam/ellixi-logo.png'
import fourInOneImage from '@images/brands/cheongdam/4in1.png'
import beforeAfterImage from '@images/brands/cheongdam/before-after.png'
import shampooTextImage from '@images/brands/cheongdam/black-change-shampoo.png'
import glitterImage from '@images/brands/cheongdam/glitter.png'
import goldBorderBox from '@images/brands/cheongdam/gold-border-box.png'
import goldLogo from '@images/brands/cheongdam/gold-logo.png'
import goldPolygonImage from '@images/brands/cheongdam/gold-polygon.png'
import gradientImage from '@images/brands/cheongdam/gradient.png'
import hsaLogo from '@images/brands/cheongdam/hsa-logo.png'
import medalImage from '@images/brands/cheongdam/medal.png'
import polygonFive from '@images/brands/cheongdam/polygon-five.png'
import product1 from '@images/brands/cheongdam/product-1.png'
import product2 from '@images/brands/cheongdam/product-2.png'
import BrandLayout from '@layouts/brand-layout'
import Head from '@modules/common/components/head'

import Image from 'next/image'
import { useQuery } from '@tanstack/react-query'
import { getBrand } from '@lib/util/brand-handler'
import Link from 'next/link'
import useAosAnimation from '@common/hooks/use-aos-animation'

const SPACING = 10

export const CONTAINER_WIDTH = {
  SUPER_LARGE: 1000,
  LARGE: 800,
  MEDIUM: 700
}

export const HeadContainer = (props: BoxProps) => {
  return <Container maxW={CONTAINER_WIDTH.LARGE} {...props} />
}

export const SubHeadContainer = (props: BoxProps) => {
  return <Container {...props} maxW={CONTAINER_WIDTH.MEDIUM} />
}

const GoldenButton = ({ children, ...props }: ButtonProps) => {
  return (
    <Button
      fontFamily="DM Sans Regular"
      variant="ghost"
      {...props}
      textTransform="uppercase"
      color="black"
      fontSize={{
        md: '28px',
        base: '20px'
      }}
      _hover={{}}
    >
      <Image src={goldBorderBox} fill alt="" />
      <Box position="relative">{children}</Box>
    </Button>
  )
}

const BorderButton = (props: ButtonProps) => {
  return (
    <Button
      fontFamily="DM Sans Regular"
      variant="ghost"
      {...props}
      textTransform="uppercase"
      background="white"
      color="black"
      borderRadius={40}
      height="12 !important"
      fontSize={18}
      pointerEvents="none"
    />
  )
}

const EffectPolygon = () => {
  return (
    <Box data-aos="fade-up" mt={SPACING}>
      <Image
        src={polygonFive}
        alt="Cheongdam result"
        width={CONTAINER_WIDTH.MEDIUM}
        height={36}
      />
    </Box>
  )
}

const Introduction = () => {
  return (
    <HeadContainer data-aos="fade-up" position="relative">
      <Box
        position="absolute"
        top={0}
        left={{
          md: 28,
          base: 0
        }}
        transform="translateY(-10%)"
      >
        <Image src={glitterImage} alt="Glitter" width={500} />
      </Box>

      <Box position="relative" zIndex={1} pt={40}>
        <VStack>
          <Image src={goldLogo} alt="Cheongdam logo" width={280} height={100} />
          <Text
            textTransform="uppercase"
            color="white"
            fontWeight="bold"
            fontSize={21}
            textAlign="center"
          >
            Black change shampoo
          </Text>
        </VStack>

        <Grid
          templateColumns="repeat(12, 1fr)"
          pt={10}
          gap={{
            md: 16,
            base: 4
          }}
        >
          <GridItem
            position="relative"
            colSpan={{
              md: 5,
              base: 12
            }}
          >
            <Box
              display={{
                base: 'flex',
                md: 'initial'
              }}
              alignItems="flex-end"
              justifyContent="center"
            >
              <HStack gap={4}>
                <Image src={product1} alt="Glitter" width={130} />
                <Image src={product2} alt="Cheongdam logo" width={130} />
              </HStack>

              <Box
                position="absolute"
                bottom={6}
                right={{
                  md: 0,
                  base: '50%'
                }}
                transform={{
                  md: 'translateX(80%)',
                  base: 'translateX(50%)'
                }}
              >
                <Box
                  position="relative"
                  width={180}
                  height={180}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Image src={goldPolygonImage} alt="Cheongdam logo" fill />

                  <Box position="relative" textAlign="center">
                    <Text textTransform="uppercase" fontWeight={600}>
                      Darkens
                    </Text>
                    <Text textTransform="uppercase" fontWeight={600}>
                      Hair in just
                    </Text>
                    <Text fontSize={80} lineHeight="80px" fontWeight="bold">
                      5
                    </Text>
                    <Text textTransform="uppercase" fontWeight={600}>
                      Minutes
                    </Text>
                  </Box>
                </Box>
              </Box>
            </Box>
          </GridItem>
          <GridItem
            colSpan={{
              md: 7,
              base: 12
            }}
            display="flex"
            flexDirection="column"
            alignItems={{
              md: 'flex-end',
              base: 'center'
            }}
          >
            <Box
              textAlign={{
                md: 'right',
                base: 'center'
              }}
            >
              <Heading
                textTransform="uppercase"
                color="white"
                fontFamily="DM Sans Regular"
                fontSize={{
                  md: '80px',
                  base: '60px'
                }}
                lineHeight={1.1}
              >
                Dark hair everyday!
              </Heading>
              <Text
                textTransform="capitalize"
                color="white"
                fontSize={{
                  md: '38px',
                  base: '28px'
                }}
              >
                No more grey hair!*
              </Text>
            </Box>

            <HStack
              gap={8}
              py={{
                md: 6,
                base: 10
              }}
            >
              <Image
                src={medalImage}
                alt="Cheongdam logo"
                width={120}
                height={150}
              />
              <Image
                src={hsaLogo}
                alt="Cheongdam logo"
                width={120}
                height={150}
              />
            </HStack>

            <Link href="/products/cheong-dam" style={{ display: 'contents' }}>
              <GoldenButton
                width={{
                  sm: '58%',
                  base: '100%'
                }}
                height={{
                  sm: 14,
                  base: 20
                }}
              >
                Buy now
              </GoldenButton>
            </Link>
          </GridItem>
        </Grid>
      </Box>
    </HeadContainer>
  )
}

const Detail = () => {
  return (
    <Box position="relative" top={-1} paddingBottom={50}>
      <Image src={gradientImage} fill alt="" />

      <SubHeadContainer
        data-aos="fade-up"
        pt={SPACING}
        display="flex"
        flexDirection="column"
        alignItems="center"
        position="relative"
      >
        <GoldenButton
          data-aos="fade-up"
          width={{
            sm: '60%',
            base: '100%'
          }}
          height={{
            sm: 24,
            base: 16
          }}
          pointerEvents="none"
        >
          All in one shampoo!
        </GoldenButton>

        <EffectPolygon />

        <Box data-aos="fade-up" mt={SPACING}>
          <Image src={fourInOneImage} alt="Cheongdam result" width={400} />
        </Box>
      </SubHeadContainer>

      <HeadContainer
        data-aos="fade-up"
        position="relative"
        display="flex"
        flexDirection="column"
        alignItems="center"
      >
        <Button
          fontFamily="DM Sans Regular"
          variant="dark"
          textTransform="uppercase"
          width="100%"
          height={{
            md: 20,
            base: 16
          }}
          fontSize={48}
          mt={SPACING}
          pointerEvents="none"
          borderRadius={0}
        >
          Shampoo
        </Button>

        <Box data-aos="fade-up" mt={SPACING}>
          <Image
            src={shampooTextImage}
            alt="Cheongdam black change shampoo"
            width={400}
          />
        </Box>
      </HeadContainer>
    </Box>
  )
}

const Cheongdam = () => {
  const { data } = useQuery(['brand', 'ellixi'], () => getBrand('ellixi'))

  useAosAnimation({
    duration: 600,
    once: false
  })

  if (!data) return null

  const { title, metadata, productSection } = data

  return (
    <>
      <Head title={title} description={metadata.meta_description} />
      <BrandLayout
        name={title}
        color={'#000'}
        brand={data}
        productSection={productSection}
      >
        {data && (
          <Box>
            <Box
              background="linear-gradient(black, #0F0B0B)"
              position="relative"
            >
              <Container
                position="relative"
                pt={{
                  base: '50px',
                  sm: '100px',
                  md: '120px'
                }}
              >
                <Introduction />

                <SubHeadContainer data-aos="fade-up" mt={SPACING}>
                  <Stack
                    direction={{
                      md: 'row',
                      base: 'column'
                    }}
                    gap={4}
                  >
                    <Box w="100%">
                      <BorderButton flex={1} width="100%">
                        As seen on TV
                      </BorderButton>
                    </Box>
                    <Box w="100%">
                      <BorderButton flex={1} width="100%">
                        Made in korea
                      </BorderButton>
                    </Box>
                  </Stack>
                </SubHeadContainer>

                <HeadContainer data-aos="fade-up">
                  <Text
                    textTransform="uppercase"
                    color="white"
                    textAlign="center"
                    marginTop={SPACING}
                    fontSize={18}
                    fontWeight={700}
                  >
                    *Shampoo with Cheongdam black change shampoo everyday for
                    best result
                  </Text>
                </HeadContainer>

                <SubHeadContainer
                  data-aos="fade-up"
                  pt={SPACING}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                >
                  <Image
                    src={beforeAfterImage}
                    alt="Cheongdam result"
                    width={CONTAINER_WIDTH.MEDIUM}
                  />
                </SubHeadContainer>
              </Container>
            </Box>

            <Detail />

            <Container maxW={CONTAINER_WIDTH.SUPER_LARGE}>
              <ExclusiveOffer />
              <ParticipatingOutlets />
              <FastResult />
              <Videos />
              <Certification />
              <HowToUse />
            </Container>
            <Tips />
            <QA />
            <Footer />
          </Box>
        )}
      </BrandLayout>
    </>
  )
}

export default Cheongdam
